import { Icons } from 'contents/icons';
import { StyledButton } from './style';
import useDarkModeStore from 'stores/useDarkModeStore';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipProps } from 'components/tooltip/Tooltip';
import { useTooltipDirectionSetting } from 'hooks/useTooltipDirectionSetting';

/**
 * 텍스트가 포함된 버튼 컴포넌트 속성
 */
export interface ButtonProps {
  parentRef?: React.RefObject<HTMLButtonElement>;
  label?: string;
  color: 'primary' | 'secondary' | 'normal' | 'strong' | 'transparent';
  type: 'solid' | 'outline' | 'text';
  size: 'large' | 'medium' | 'small';
  error?: boolean;
  width?: string;
  height?: string;
  maxWidth?: string;
  preIcon?: keyof typeof Icons;
  postIcon?: keyof typeof Icons;
  readonly?: boolean;
  disabled?: boolean;
  selected?: boolean;
  className?: string;
  tooltip?: TooltipProps;
  sx?: CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onClickCapture?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDownCapture?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseUp?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMount?: () => void;
  onUnmount?: () => void;
}

/**
 * Button component
 * @author hc_yang
 * @param {ButtonProps} param
 * @returns JSX.Element
 */
export const Button = ({
  parentRef,
  label,
  color,
  type,
  size,
  error,
  width,
  height,
  maxWidth,
  preIcon,
  postIcon,
  readonly,
  disabled,
  selected,
  className,
  tooltip,
  sx,
  onClick,
  onClickCapture,
  onBlur,
  onFocus,
  onMouseDown,
  onMouseDownCapture,
  onMouseUp,
  onMouseEnter,
  onMouseLeave,
  onMount,
  onUnmount,
}: ButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const PreIcon = preIcon && Icons[preIcon];
  const PostIcon = postIcon && Icons[postIcon];
  const { isDarkMode } = useDarkModeStore();

  const { decideTooltipDirection } = useTooltipDirectionSetting();

  useEffect(() => {
    onMount && onMount();
    return () => {
      onUnmount && onUnmount();
    };
  }, []);

  return (
    <StyledButton
      ref={parentRef || buttonRef}
      $color={color}
      $size={size}
      $type={error ? 'outline' : type}
      $width={width || 'max-content'}
      $error={error}
      $height={height}
      $maxWidth={maxWidth}
      $dark={isDarkMode}
      $readonly={readonly}
      disabled={disabled}
      className={`${className ? className : ''} ${selected ? 'selected' : ''}`}
      onClick={onClick}
      onClickCapture={onClickCapture}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseDown={onMouseDown}
      onMouseDownCapture={onMouseDownCapture}
      onMouseUp={onMouseUp}
      onMouseEnter={(e) => {
        setIsHovered(true);
        onMouseEnter && onMouseEnter(e);
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        onMouseLeave && onMouseLeave(e);
      }}
      style={sx}
    >
      {PreIcon && <PreIcon className="icon" />}
      {label && <p>{label}</p>}
      {PostIcon && <PostIcon className="icon" />}
      {isHovered && tooltip && (
        <Tooltip
          {...tooltip}
          parentRef={parentRef || buttonRef}
          direction={decideTooltipDirection({
            rect:
              parentRef?.current?.getBoundingClientRect() ||
              buttonRef.current?.getBoundingClientRect(),
            defaultDirection: tooltip.direction,
          })}
        />
      )}
    </StyledButton>
  );
};
